import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  onClose: () => void;
  sourceHtml: string;
  onApply: (html: string, count: number) => void;
  onClear?: () => void;
  onFocusMatch?: (index: number) => void;
  containerSelector?: string;
  onNoResults?: (query: string) => void;
  inline?: boolean;
};

export const FindInHtml: React.FC<Props> = ({ open, onClose, sourceHtml, onApply, onClear, onFocusMatch, containerSelector, onNoResults, inline = false }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [query, setQuery] = useState('');
  const [lastSearchedQuery, setLastSearchedQuery] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      // small delay to ensure dialog/inline element is mounted
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open, inline]);

  const highlightMatchesInHtml = useCallback((html: string, q: string): { html: string; count: number } => {
    if (!q) return { html, count: 0 };
    try {
      const div = document.createElement('div');
      div.innerHTML = html || '';
      const lowerQ = q.toLowerCase();
      let count = 0;

      const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue || '';
          const lower = text.toLowerCase();
          let idx = lower.indexOf(lowerQ);
          if (idx === -1) return;
          const frag = document.createDocumentFragment();
          let last = 0;
          while (idx > -1) {
            const before = text.substring(last, idx);
            if (before) frag.appendChild(document.createTextNode(before));
            const mark = document.createElement('mark');
            mark.setAttribute('data-find-index', String(count));
            mark.textContent = text.substr(idx, q.length);
            frag.appendChild(mark);
            count++;
            last = idx + q.length;
            idx = lower.indexOf(lowerQ, last);
          }
          const after = text.substring(last);
          if (after) frag.appendChild(document.createTextNode(after));
          node.parentNode?.replaceChild(frag, node);
        } else {
          let child = node.firstChild;
          while (child) {
            const next = child.nextSibling;
            walk(child);
            child = next;
          }
        }
      };

      walk(div);
      return { html: div.innerHTML, count };
    } catch (err) {
      console.error('highlightMatchesInHtml err', err);
      return { html, count: 0 };
    }
  }, []);

  const focusMatch = useCallback((index: number): void => {
    try {
      setTimeout(() => {
        let sel: Element | null = null;
        if (containerSelector) {
          const root = document.querySelector(containerSelector);
          sel = root ? root.querySelector(`[data-find-index="${index}"]`) : document.querySelector(`[data-find-index="${index}"]`);
        } else {
          sel = document.querySelector(`[data-find-index="${index}"]`);
        }
        if (sel) {
          (sel as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (onFocusMatch) onFocusMatch(index);
        }
      }, 120);
    } catch (err) { console.error('focusMatch err', err); }
  }, [containerSelector, onFocusMatch]);

  const performFind = useCallback((qRaw?: string): void => {
    const q = ((qRaw ?? query) || '').trim();
    if (!q) return;
    const { html, count } = highlightMatchesInHtml(sourceHtml || '', q);
    if (count > 0) {
      setMatchCount(count);
      setCurrentIndex(0);
      setLastSearchedQuery(q.toLowerCase());
      onApply(html, count);
      focusMatch(0);
    }
    else {
      // Clear previous matches/highlights and reset internal state
      setMatchCount(0);
      setCurrentIndex(0);
      setLastSearchedQuery('');
      if (onClear) onClear();
      try {
        if (typeof onNoResults === 'function') onNoResults(q);
        else enqueueSnackbar(t('notes.findNoResults') || 'No results', { variant: 'info' });
      } catch (err) { /* ignore */ }
    }
  }, [query, highlightMatchesInHtml, sourceHtml, onApply, focusMatch, onNoResults, enqueueSnackbar, t, onClear]);

  const gotoNext = useCallback(() => {
    if (matchCount === 0) return;
    const next = (currentIndex + 1) % matchCount;
    setCurrentIndex(next);
    focusMatch(next);
  }, [currentIndex, matchCount, focusMatch]);

  const gotoPrev = useCallback(() => {
    if (matchCount === 0) return;
    const prev = (currentIndex - 1 + matchCount) % matchCount;
    setCurrentIndex(prev);
    focusMatch(prev);
  }, [currentIndex, matchCount, focusMatch]);

  const handleClear = useCallback(() => {
    setQuery('');
    setLastSearchedQuery('');
    setMatchCount(0);
    setCurrentIndex(0);
    if (onClear) onClear();
  }, [onClear]);

  const content = (
    <>
      <DialogTitle>{t('notes.find') || 'Find in note'}</DialogTitle>
      <DialogContent>
        <TextField
          inputRef={inputRef}
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (matchCount && matchCount > 0 && query.trim().toLowerCase() === lastSearchedQuery) {
                gotoNext();
              } else if (query && query.trim()) {
                performFind(query);
              }
            }
          }}
          placeholder={t('notes.findPlaceholder') || 'Enter search term'}
          sx={{ my: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button onClick={() => performFind(query)}>{t('common.find') || 'Find'}</Button>
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
          <Button onClick={gotoNext} disabled={matchCount === 0}>{t('common.next') || 'Next'}</Button>
          <Button onClick={gotoPrev} disabled={matchCount === 0}>{t('common.back') || 'Back'}</Button>
          <Typography variant="body2" sx={{ ml: 1 }}>{matchCount ? `${currentIndex + 1} / ${matchCount}` : t('notes.findNoResults') || 'No results'}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { handleClear(); }}>{t('common.clear') || 'Clear'}</Button>
        <Button onClick={onClose}>{t('common.close') || 'Close'}</Button>
      </DialogActions>
    </>
  );

  if (inline) {
    // Only render the floating panel when explicitly opened
    if (!open) return null;
    return (
      <Paper sx={{ position: 'fixed', top: 80, right: 24, width: 360, zIndex: 1400, p: 1, bgcolor: 'background.paper' }}>
        {content}
      </Paper>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {content}
    </Dialog>
  );
};

export default FindInHtml;
